import React from 'react';
import { Pressable, StyleSheet, TextInput, View } from 'react-native';
import { Text } from './Texte';
import { Button } from './Button';
import { X } from './icons';
import { colors, formes, radius, shadow, spacing, typography } from '../theme/tokens';
import type { CefrLevel, LanguageEntry, OutputLanguage } from '../types';
import { nativeLevelLabel, quickLanguageNames } from '../utils/documentLabels';
import { t } from '../i18n';

// Les codes CEFR sont universels ; seul « Natif » est du texte, et il suit le DOCUMENT.
const CEFR_CODES: CefrLevel[] = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];



// Diplômes les plus reconnus par langue (sélectionnables ; saisie libre conservée).
const DIPLOMA_SUGGESTIONS: Record<string, string[]> = {
  anglais: ['TOEFL', 'TOEIC', 'IELTS', 'Cambridge'],
  espagnol: ['DELE', 'SIELE'],
  allemand: ['Goethe-Zertifikat', 'TestDaF'],
  francais: ['DELF', 'DALF'],
  italien: ['CILS', 'CELI'],
};

function normalize(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

export function diplomaSuggestions(name: string): string[] {
  return DIPLOMA_SUGGESTIONS[normalize(name)] ?? [];
}

// Sérialise les langues en texte étiqueté clair pour Claude (réponse Q5).
export function serializeLanguages(langs: LanguageEntry[]): string {
  return langs
    .filter((l) => l.name.trim())
    .map((l) => {
      const diploma = l.diploma?.trim() ? ` (${l.diploma.trim()})` : '';
      return `${l.name.trim()} - ${l.level}${diploma}`;
    })
    .join(' ; ');
}

interface LanguagePickerProps {
  // Langue du DOCUMENT : les noms de langues suggérés et le niveau « natif » sont recopiés dans le
  // CV et partent dans le prompt. Ils suivent donc le document, pas l'interface — à la différence
  // des libellés de ce composant (placeholder, « Niveau », bouton d'ajout), qui suivent l'interface.
  outputLanguage?: OutputLanguage;
  value: LanguageEntry[];
  onChange: (langs: LanguageEntry[]) => void;
  testID?: string;
}

export function LanguagePicker({ value, onChange, outputLanguage = 'fr', testID }: LanguagePickerProps) {
  const QUICK_LANGUAGES = quickLanguageNames(outputLanguage);
  const LEVELS: CefrLevel[] = [...CEFR_CODES, nativeLevelLabel(outputLanguage) as CefrLevel];
  const update = (index: number, patch: Partial<LanguageEntry>) =>
    onChange(value.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)));

  const add = (name = '') => onChange([...value, { name, level: 'B2' }]);

  const remove = (index: number) => onChange(value.filter((_, i) => i !== index));

  return (
    <View testID={testID} style={styles.container}>
      {value.map((entry, index) => {
        const suggestions = diplomaSuggestions(entry.name);
        return (
          <View key={index} testID={`lang-row-${index}`} style={styles.row}>
            <View style={styles.rowHeader}>
              <TextInput
                testID={`lang-name-${index}`}
                style={styles.name}
                placeholder={t('languagePicker.namePlaceholder')}
                placeholderTextColor={colors.grey400}
                value={entry.name}
                onChangeText={(name) => update(index, { name })}
              />
              <Pressable
                testID={`lang-remove-${index}`}
                accessibilityRole="button"
                accessibilityLabel={t('a11y.removeLanguage')}
                onPress={() => remove(index)}
                style={styles.remove}
              >
                <X size={18} color={colors.grey500} />
              </Pressable>
            </View>

            <Text style={styles.miniLabel}>{t('languagePicker.level')}</Text>
            <View style={styles.chipsRow}>
              {LEVELS.map((level) => {
                const active = entry.level === level;
                return (
                  <Pressable
                    key={level}
                    testID={`lang-level-${index}-${level}`}
                    accessibilityRole="button"
                    accessibilityState={{ selected: active }}
                    onPress={() => update(index, { level })}
                    style={[styles.chip, active && styles.chipActive]}
                  >
                    <Text style={[styles.chipText, active && styles.chipTextActive]}>{level}</Text>
                  </Pressable>
                );
              })}
            </View>

            <Text style={styles.miniLabel}>{t('languagePicker.diploma')}</Text>
            {suggestions.length > 0 ? (
              <View style={styles.chipsRow}>
                {suggestions.map((diploma) => {
                  const active = entry.diploma === diploma;
                  return (
                    <Pressable
                      key={diploma}
                      testID={`lang-diploma-sugg-${index}-${diploma}`}
                      accessibilityRole="button"
                      onPress={() => update(index, { diploma })}
                      style={[styles.chip, active && styles.chipActive]}
                    >
                      <Text style={[styles.chipText, active && styles.chipTextActive]}>{diploma}</Text>
                    </Pressable>
                  );
                })}
              </View>
            ) : null}
            <TextInput
              testID={`lang-diploma-${index}`}
              style={styles.diploma}
              placeholder={t('languagePicker.diplomaPlaceholder')}
              placeholderTextColor={colors.grey400}
              value={entry.diploma ?? ''}
              onChangeText={(diploma) => update(index, { diploma })}
            />
          </View>
        );
      })}

      <View style={styles.quickRow}>
        {QUICK_LANGUAGES.map((name) => (
          <Pressable
            key={name}
            testID={`lang-quick-${name}`}
            accessibilityRole="button"
            onPress={() => add(name)}
            style={styles.quick}
          >
            <Text style={styles.quickText}>+ {name}</Text>
          </Pressable>
        ))}
      </View>
      <Button title={t('languagePicker.add')} variant="secondary" onPress={() => add()} testID="lang-add" />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { gap: spacing.md },
  row: {
    gap: spacing.sm,
    backgroundColor: colors.white,
    borderRadius: formes.rayon.md,
    padding: spacing.md,
    ...shadow.card,
  },
  rowHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  name: {
    flex: 1,
    height: 40,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey200,
    fontSize: typography.bodyMd.size,
    color: colors.grey900,
  },
  remove: { minWidth: formes.cible, minHeight: formes.cible, alignItems: 'flex-end', justifyContent: 'center' },
  miniLabel: {
    fontSize: typography.caption.size,
    fontWeight: typography.caption.weight,
    letterSpacing: typography.caption.letterSpacing,
    color: colors.grey500,
  },
  chipsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  chip: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: radius.pill,
    backgroundColor: colors.grey100,
  },
  chipActive: { backgroundColor: colors.bluePrimary },
  chipText: { fontSize: typography.caption.size, color: colors.grey700 },
  chipTextActive: { color: colors.white },
  diploma: {
    height: 40,
    borderWidth: 1,
    borderColor: colors.grey200,
    borderRadius: radius.sm,
    paddingHorizontal: spacing.md,
    fontSize: typography.small.size,
    color: colors.grey900,
  },
  quickRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  quick: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: radius.pill,
    backgroundColor: colors.blueSoft,
  },
  quickText: { fontSize: typography.caption.size, color: colors.bluePrimary },
});
